import type { ElementType } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { BasicTitle, BiggerSubtitle } from '@genially/design-system';

const shakeTop = keyframes({
  '0%, 100%': {
    transform: 'rotate(0deg)',
    transformOrigin: '50% 0',
  },
  '10%': {
    transform: 'rotate(4deg)',
  },
  '20%, 40%, 60%': {
    transform: 'rotate(-8deg)',
  },
  '30%, 50%, 70%': {
    transform: 'rotate(8deg)',
  },
  '80%': {
    transform: 'rotate(-4deg)',
  },
  '90%': {
    transform: 'rotate(4deg)',
  },
});

const textFocusIn = keyframes({
  '0%': {
    filter: 'blur(12px)',
    opacity: 0,
  },
  '100%': {
    filter: 'blur(0px)',
    opacity: 1,
  },
});

const slideInTop = keyframes({
  '0%': {
    transform: 'translateY(-100%)',
    opacity: 0,
  },
  '100%': {
    transform: 'translateY(0)',
    opacity: 1,
  },
});

const withAnimationTitle = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${textFocusIn} 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
        animation-delay: 0.75s;
      `,
  );

const withAnimationSubtitle = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${slideInTop} 0.3125s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
        animation-delay: 1s;
      `,
  );

const withAnimationImage = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${shakeTop} 0.375s ease-out both;
        animation-delay: 0.375s;
      `,
  );

export const StyledImage = withAnimationImage(
  styled.img(({ theme: { size } }) => ({
    width: size.units(22.5),
  })),
);

export const StyledBasicTitle = withAnimationTitle(
  styled(BasicTitle)(({ theme: { color } }) => ({
    color: color.content.reversed.default(),
    textAlign: 'center',
  })),
);

export const StyledBiggerSubtitle = withAnimationSubtitle(
  styled(BiggerSubtitle)(({ theme: { color, size } }) => ({
    color: color.content.reversed.default(),
    textAlign: 'center',
    padding: size.units(1.5),
  })),
);

const fadeOut = keyframes({
  '0%': {
    opacity: 1,
  },
  '100%': {
    opacity: 0,
  },
});

const withAnimationWrapper = (Component: ElementType) =>
  styled(Component)<{ $activeHideAnimation: boolean }>(
    ({ $activeHideAnimation }) =>
      $activeHideAnimation &&
      css`
        animation: ${fadeOut} 0.625s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
      `,
  );

export const Wrapper = withAnimationWrapper(
  styled.div(({ theme: { size } }) => ({
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    [StyledBasicTitle]: {
      marginTop: size.units(3),
    },

    [StyledBiggerSubtitle]: {
      marginTop: size.units(1.5),
    },
  })),
);
